import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { SeoService } from 'src/app/shared/seo.service';
import { LiveSessionSchemaBuilder } from 'src/app/shared/live-session-schema.builder';
import { Title, Meta } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { CommonService } from 'src/app/shared/common.service';
import { ToastService } from 'src/app/shared/toast.service';
import * as moment from 'moment-timezone';


type FilterMode = 'all' | 'upcoming' | 'past';

interface SessionTime {
  id?: string;
  date?: string | Date;
  startTime?: string;
  endTime?: string;
  timezone?: string;
}

interface LiveEvent {
  _id: string;
  title: string;
  description: string;
  sessions: SessionTime[];
  alreadyRegistered: boolean;
}

export interface TextStyle {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number;
  lineHeight?: number;
  letterSpacing?: number;
  color?: string;
  textAlign?: 'left' | 'center' | 'right';
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  maxWidth?: number;
}

export interface LayoutStyle {
  paddingTop?: number;
  paddingBottom?: number;
  marginTop?: number;
  marginBottom?: number;
  contentMaxWidth?: number;
  textAlign?: 'left' | 'center' | 'right';
  offsetX?: number;
  offsetY?: number;
}

interface LiveSessionsPublicContent {
  miscTexts?: {
    heroStrongText?: string;
    zoomWord?: string;
  };

  hero: {
    eyebrow: string;
    headline: string; // \n supported
    paragraph: string;
    badges: string[];

    eyebrowStyle?: TextStyle;
    headlineStyle?: TextStyle;
    paragraphStyle?: TextStyle;
    badgesStyle?: TextStyle;

    headlineEmphasisStyle?: TextStyle;
    layoutStyle?: LayoutStyle;
  };

  nextBand?: {
  labelText: string;
  viaZoomText: string;
  countdownLabels: { days: string; hrs: string; mins: string; secs: string };

  labelStyle?: TextStyle;
  headlineStyle?: TextStyle;
  headlineEmphasisStyle?: TextStyle;
  metaStyle?: TextStyle;

  countdownNumberStyle?: TextStyle;
  countdownLabelStyle?: TextStyle;
  countdownSeparatorStyle?: TextStyle;

  nextSessionCardStyle?: any;
  countdownBoxStyle?: any;
  reserveButtonStyle?: any;

  layoutStyle?: LayoutStyle;
};

  sessionsSection: {
  eyebrow: string;
  headline: string;
  paragraph: string;

  allSessionsTabText: string;
  nextSessionSubtitle: string;

  reserveButtonText: string;
  registeredText: string;
  concludedText: string;
  emptyStateText: string;

  statusLabels?: { nextUp: string; upcoming: string; past: string };
  viaZoomText?: string;

  eyebrowStyle?: TextStyle;
  headlineStyle?: TextStyle;
  paragraphStyle?: TextStyle;

  headlineEmphasisStyle?: TextStyle;
  layoutStyle?: LayoutStyle;

  cardStyle?: any;
  featuredCardStyle?: any;
  topBarStyle?: any;

  badgeStyle?: TextStyle;
  badgeNextStyle?: any;
  badgeUpcomingStyle?: any;
  badgePastStyle?: any;

  dateBlockStyle?: any;
  dateMonthStyle?: TextStyle;
  dateDayStyle?: TextStyle;

  timeStyle?: TextStyle;
  timeStrongStyle?: TextStyle;

  titleStyle?: TextStyle;
  descriptionStyle?: TextStyle;

  hostNamesStyle?: TextStyle;
  hostAvatarStyle?: any;

  reserveButtonStyle?: any;
  reserveSecondaryButtonStyle?: any;
  pastButtonStyle?: any;
  registeredTextStyle?: TextStyle;

  emptyStateStyle?: any;
};

  expectSection: {
  eyebrow: string;
  headline: string;
  cards: Array<{
    num: string;
    title: string;
    body: string;
    cardStyle?: any;
    numStyle?: TextStyle;
    titleStyle?: TextStyle;
    bodyStyle?: TextStyle;
  }>;

  eyebrowStyle?: TextStyle;
  headlineStyle?: TextStyle;
  headlineEmphasisStyle?: TextStyle;
  layoutStyle?: LayoutStyle;
};

  hostsSection: {
  eyebrow: string;
  headline: string;
  hosts: Array<{
    initials: string;
    name: string;
    cred: string;
    bio: string;
    cardStyle?: any;
    avatarStyle?: any;
    nameStyle?: TextStyle;
    credStyle?: TextStyle;
    bioStyle?: TextStyle;
  }>;

  eyebrowStyle?: TextStyle;
  headlineStyle?: TextStyle;
  headlineEmphasisStyle?: TextStyle;
  layoutStyle?: LayoutStyle;
};

  finalCta: {
  eyebrow: string;
  headline: string;
  paragraph: string;
  note: string;

  primaryBtnText: string;
  secondaryBtnText: string;
  secondaryBtnUrl: string;

  eyebrowStyle?: TextStyle;
  headlineStyle?: TextStyle;
  paragraphStyle?: TextStyle;
  noteStyle?: TextStyle;

  headlineEmphasisStyle?: TextStyle;
  primaryBtnStyle?: any;
  secondaryBtnStyle?: any;
  layoutStyle?: LayoutStyle;
};

  signupModal: {
  registeringForLabel: string;

  headline: string;
  subText: string;

  firstNameLabel: string;
  lastNameLabel: string;
  emailLabel: string;

  firstNamePlaceholder: string;
  lastNamePlaceholder: string;
  emailPlaceholder: string;

  confirmBtnText: string;
  noteText: string;

  successHeadline: string;
  successBody: string;
  closeBtnText: string;

  miniSessionLabel: string;
  miniDateLabel: string;
  miniTimeLabel: string;
  miniEmailLabel: string;

  headlineStyle?: TextStyle;
  subTextStyle?: TextStyle;
  successHeadlineStyle?: TextStyle;
  successBodyStyle?: TextStyle;

  headlineEmphasisStyle?: TextStyle;
  successHeadlineEmphasisStyle?: TextStyle;

  modalStyle?: any;
  sessionMetaStyle?: any;
  labelStyle?: TextStyle;
  inputStyle?: any;
  confirmBtnStyle?: any;
  closeBtnStyle?: any;
  noteStyle?: TextStyle;
  successBoxStyle?: any;
};
}

@Component({
  selector: 'app-liv-sessionpage',
  templateUrl: './liv-sessionpage.component.html',
  styleUrls: ['./liv-sessionpage.component.scss']
})
export class LivSessionpageComponent implements OnInit, AfterViewInit, OnDestroy {
  isNavScrolled = false;
  heroHeadlineLines: Array<{ before: string; em: string; after: string }> = [];

  allSessions: LiveEvent[] = [];
  filteredSessions: LiveEvent[] = [];
  activeFilter: FilterMode = 'all';

  nextSession: LiveEvent | null = null;

  trackBySession(index: number, s: any): string {
    return s?._id || s?.id || String(index);
  }
  trackByIndex(index: number): number {
    return index;
  }

  countdown = { days: '--', hrs: '--', mins: '--', secs: '--' };
  countdownSummary = ''; 
  private countdownTimer: any = null;

  showPopup = false;
  selectedSession: LiveEvent | null = null;

  firstName = '';
  lastName = '';
  email = '';

  modalStep: 'form' | 'success' = 'form';

  successTitle = '';
  successDate = '';
  successTime = '';
  successEmail = '';

  isSubmitting: boolean = false;
emailError: boolean = false;
humanCheckNum1: number = 0;
humanCheckNum2: number = 0;
humanCheckAnswer: number | null = null;
humanCheckError: boolean = false;
humanCheckRequiredError: boolean = false;

emailFollowUp: boolean = false;
  // reveal flags
  revealHero = false;
  revealNext = false;
  revealSessionsHeader = false;
  revealTabs = false;
  revealGrid = false;
  revealExpect = false;
  revealExpectGrid = false;
  revealHosts = false;
  revealHostsGrid = false;
  revealCta = false;

  pageContent: LiveSessionsPublicContent = this.getDefaultContent();

  // computed headline split parts (to keep <em> structure)
  heroLine1 = 'Your questions,';
  heroLine2Prefix = 'answered';
  heroEmphasis = 'live.';

  sessionsHeadlineLines: Array<{ before: string; em: string; after: string }> = [];

  expectHeadlinePrefix = 'What to';
  expectHeadlineEmphasis = 'expect';

  hostsHeadlinePrefix = "Who you'll be";
  hostsHeadlineEmphasis = 'learning from';

  finalHeadlinePrefix = 'Reserve your';
  finalHeadlineEmphasis = 'free seat.';

  modalHeadlinePrefix = 'Reserve your';
  modalHeadlineEmphasis = 'free spot.';

  successHeadlinePrefix = "You're";
  successHeadlineEmphasis = 'registered.';

  

  constructor(
    private commonService: CommonService,
    private toast: ToastService,
    private router: Router,
    private seoService: SeoService 
  ) {}

  ngOnInit(): void {
     this.seoService.applySeo({
    title: 'Live Sessions & Webinars | NEXGEN Coaching Academy',
    description: 'Free 60-min live sessions with ICF-certified faculty. Ask about ACC & PCC credentials, program costs, and schedules. Zero pressure — reserve your spot today.',
    keywords: 'live coaching webinars, NEXGEN info sessions, executive coaching live session, ICF certification webinar, NEXGEN free webinar, free ICF coaching session, executive coaching Q&A',
    canonicalUrl: 'https://academy.nexgencoaches.com/live-session',
    ogTitle: 'Free Live Sessions & Webinars | NEXGEN Executive Coaching Academy',
    ogDescription: 'Join a free 60-minute live info session with NEXGEN Executive Coaching Academy. Ask questions about ICF ACC & PCC certification, program structure, and costs. No credit card required.',
    ogImage: 'https://nexgencoaches.com/assets/og-live-sessions.jpg',
    ogType: 'website',
    twitterTitle: 'Free Live Sessions | NEXGEN Executive Coaching',
    twitterDescription: 'Attend a free 60-min live session with NEXGEN faculty. ICF certification Q&A, program overview, zero pressure. Reserve your spot.',
    robots: 'index, follow'
    // NOTE: schema is NOT passed here — it is injected after sessions
    // load in loadSessions() below, so Event schemas have real dates.
  });
  this.loadSessions(this.getSessionIdFromUrl());
  this.loadPublicPageContent();

  // Register reCAPTCHA global callbacks
  // (window as any)['onRecaptchaSuccess'] = (token: string) => {
  //   this.recaptchaToken = token;
  //   this.captchaError = false;
  // };
  // (window as any)['onRecaptchaExpired'] = () => {
  //   this.recaptchaToken = null;
  // };

  this.router.events.subscribe((event) => {
    if (event instanceof NavigationEnd) {
      const state: any = history.state;
      if (state?.scrollToLiveSession) {
        setTimeout(() => this.scrollTo('sessions'), 150);
      }
    }
  });
}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.revealHero = true;
      this.revealSessionsHeader = true;
      this.revealTabs = true;
      this.revealGrid = true;
      this.revealExpect = true;
      this.revealExpectGrid = true;
      this.revealHosts = true;
      this.revealHostsGrid = true;
      this.revealCta = true;
      this.revealNext = !!this.nextSession;
    }, 120);
  }

  ngOnDestroy(): void {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.seoService.clearSeo(); 
  }

  @HostListener('window:scroll')
  onScroll(): void {
    this.isNavScrolled = window.scrollY > 40;
  }

  scrollTo(id: string, ev?: Event): void {
    if (ev) ev.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private loadPublicPageContent(): void {
    this.commonService.getLiveSessionsPageContentPublic().subscribe({
      next: (res: any) => {
        if (res?.status === 1 && res?.data) {
          const incoming = res.data as LiveSessionsPublicContent;
          this.pageContent = this.mergeWithDefaults(this.getDefaultContent(), incoming);

          this.applyHeroHeadlineParts(this.pageContent?.hero?.headline || '');
          this.applySectionHeadlineParts();
        } else {
          this.pageContent = this.getDefaultContent();
          this.applyHeroHeadlineParts(this.pageContent.hero.headline);
          this.applySectionHeadlineParts();
        }
      },
      error: () => {
        this.pageContent = this.getDefaultContent();
        this.applyHeroHeadlineParts(this.pageContent.hero.headline);
        this.applySectionHeadlineParts();
      }
    });
  }

  private getDefaultContent(): LiveSessionsPublicContent {
    return {
      miscTexts: {
        heroStrongText: 'Pick a date and reserve your seat.',
        zoomWord: 'Zoom'
      },

      hero: {
  eyebrow: 'Free Live Sessions',
  headline: 'Your questions,\nanswered <em>live</em>.', // Added <em> tags
  paragraph: "Every session is free. No sales pitch, no obligation. Just an honest conversation about what NexGen is, how the programs work, and whether this is the right next step for you.",
  badges: ['Free to attend', 'Live Q&A with faculty', 'Zero pressure', '60 minutes']
},

      nextBand: {
        labelText: 'Next Session — Coming Up',
        viaZoomText: 'Via Zoom',
        countdownLabels: { days: 'Days', hrs: 'Hrs', mins: 'Min', secs: 'Sec' }
      },

      sessionsSection: {
        eyebrow: 'Upcoming & Past Sessions',
        headline: 'Choose your session.',
        paragraph:
          'All sessions cover the same core content — choose the date that works best for your schedule. New sessions are added every month.',
        allSessionsTabText: 'All Sessions',
        nextSessionSubtitle: '',
        reserveButtonText: 'Reserve My Spot',
        registeredText: "You're registered!",
        concludedText: 'Session Concluded',
        emptyStateText: 'No sessions found.',
        statusLabels: { nextUp: 'Next Up', upcoming: 'Upcoming', past: 'Past Session' },
        viaZoomText: 'Via Zoom'
      },

      expectSection: {
        eyebrow: 'What Happens on the Call',
        headline: 'What to expect',
        cards: [
          { num: '01', title: 'Program Overview', body: 'We walk through the NexGen model — what we teach, how it’s structured, and why we built it this way.' },
          { num: '02', title: 'ICF Pathway Explained', body: 'We break down ACC and PCC requirements in plain language. You’ll leave knowing exactly what each credential takes.' },
          { num: '03', title: 'Live Q&A', body: 'The last third of every session is open Q&A. Ask anything — programs, industry, your situation, or next steps.' }
        ]
      },

      hostsSection: {
        eyebrow: 'Your Hosts',
        headline: "Who you'll be learning from",
        hosts: [
          { initials: 'L', name: 'Lyssa deHart', cred: 'LICSW, MCC · Coaching Craft', bio: 'Master Certified Coach, 10,000+ coaches trained. Author and podcast host.' },
          { initials: 'J', name: 'Jake Mannino', cred: 'MBA, PCC · Business Building', bio: 'Executive leader at Microsoft overseeing a $1B+ organization.' }
        ]
      },

      finalCta: {
        eyebrow: 'Ready to Join?',
        headline: 'Reserve your free seat.',
        paragraph:
          "Pick a session that works for your schedule. It's free, it's live, and there's no obligation to enroll. Come with questions.",
        note: 'Free to attend · 60 minutes · Live Q&A with faculty',
        primaryBtnText: 'Reserve Next Session',
        secondaryBtnText: 'View Programs',
        secondaryBtnUrl: 'https://nexgencoaches.com/programs/'
      },

      signupModal: {
        registeringForLabel: "You're registering for",
        headline: 'Reserve your free spot.',
        subText:
          "Fill in your details below and we'll send your confirmation and Zoom link by email. No payment required.",
        firstNameLabel: 'First Name',
        lastNameLabel: 'Last Name',
        emailLabel: 'Email Address',
        firstNamePlaceholder: 'Jane',
        lastNamePlaceholder: 'Smith',
        emailPlaceholder: 'jane@example.com',
        confirmBtnText: 'Confirm My Spot',
        noteText:
          "Free to attend. No credit card required. You'll receive a Zoom link by email.",
        successHeadline: "You're registered.",
        successBody:
          "Check your inbox — your confirmation and Zoom link are on their way. We look forward to seeing you live.",
        closeBtnText: 'Close',
        miniSessionLabel: 'Session:',
        miniDateLabel: 'Date:',
        miniTimeLabel: 'Time:',
        miniEmailLabel: 'Email:'
      }
    };
  }

  private mergeWithDefaults<T>(defaults: T, incoming: any): T {
    const isObj = (v: any) => v && typeof v === 'object' && !Array.isArray(v);

    const merge = (d: any, i: any): any => {
      if (Array.isArray(d)) return Array.isArray(i) && i.length ? i : d;
      if (isObj(d)) {
        const out: any = { ...d };
        Object.keys(out).forEach((k) => (out[k] = merge(d[k], i?.[k])));
        if (isObj(i)) Object.keys(i).forEach((k) => { if (!(k in out)) out[k] = i[k]; });
        return out;
      }
      if (i === null || i === undefined) return d;
      if (typeof i === 'string' && i.trim() === '') return d;
      return i;
    };

    return merge(defaults, incoming);
  }

  private applyHeroHeadlineParts(headline: string): void {
      this.heroHeadlineLines = this.parseHeadlineLines(headline);
    const raw = String(headline || '').trim();
    const parts = raw.split('\n');

    const line1 = (parts[0] || 'Your questions,').trim();
    const line2 = (parts[1] || 'answered live.').trim();

    const { prefix, emphasis } = this.splitLastWord(line2);

    this.heroLine1 = line1 || 'Your questions,';
    this.heroLine2Prefix = prefix || 'answered';
    this.heroEmphasis = emphasis || 'live.';
  }

  private applySectionHeadlineParts(): void {
  const s = this.pageContent?.sessionsSection?.headline || 'Choose your session.';
  this.sessionsHeadlineLines = this.parseHeadlineLines(s);

  const e = this.pageContent?.expectSection?.headline || 'What to expect';
  {
    const { prefix, emphasis } = this.splitLastWord(e);
    this.expectHeadlinePrefix = prefix || 'What to';
    this.expectHeadlineEmphasis = emphasis || 'expect';
  }

  const h = this.pageContent?.hostsSection?.headline || "Who you'll be learning from";
  {
    const { prefix, emphasis } = this.splitLastNWords(h, 2);
    this.hostsHeadlinePrefix = prefix || "Who you'll be";
    this.hostsHeadlineEmphasis = emphasis || 'learning from';
  }

  const f = this.pageContent?.finalCta?.headline || 'Reserve your free seat.';
  {
    const { prefix, emphasis } = this.splitLastNWords(f, 2);
    this.finalHeadlinePrefix = prefix || 'Reserve your';
    this.finalHeadlineEmphasis = emphasis || 'free seat.';
  }

  const m = this.pageContent?.signupModal?.headline || 'Reserve your free spot.';
  {
    const { prefix, emphasis } = this.splitLastNWords(m, 2);
    this.modalHeadlinePrefix = prefix || 'Reserve your';
    this.modalHeadlineEmphasis = emphasis || 'free spot.';
  }

  const sh = this.pageContent?.signupModal?.successHeadline || "You're registered.";
  {
    const { prefix, emphasis } = this.splitLastWord(sh);
    this.successHeadlinePrefix = prefix || "You're";
    this.successHeadlineEmphasis = emphasis || 'registered.';
  }
}

  private splitLastWord(text: string): { prefix: string; emphasis: string } {
    const t = String(text || '').trim().replace(/\s+/g, ' ');
    const tokens = t.split(' ').filter(Boolean);
    if (tokens.length <= 1) return { prefix: '', emphasis: t };
    return { prefix: tokens.slice(0, -1).join(' '), emphasis: tokens[tokens.length - 1] };
  }

  private splitLastNWords(text: string, n: number): { prefix: string; emphasis: string } {
    const t = String(text || '').trim().replace(/\s+/g, ' ');
    const tokens = t.split(' ').filter(Boolean);
    if (tokens.length <= n) return { prefix: '', emphasis: t };
    return { prefix: tokens.slice(0, -n).join(' '), emphasis: tokens.slice(-n).join(' ') };
  }

  // ===== LOAD SESSIONS =====
  loadSessions(autoOpenSessionId?: string | null): void {
  this.commonService.getFreeEventSessions().subscribe({
    next: (res: any) => {
      if (res?.status === 1) {
        this.allSessions = (res.data || []).map((event: any) => ({
          _id: event._id,
          title: event.title,
          description: event.description,
          sessions: event.sessions || [],
          alreadyRegistered: false
        }));

        this.allSessions.sort((a, b) => {
          const aTime = this.getSessionStartDate(a)?.valueOf() || 0;
          const bTime = this.getSessionStartDate(b)?.valueOf() || 0;
          return aTime - bTime;
        });

        this.pickNextSession();
        this.applyFilter();
        this.injectPageSchema();

        if (autoOpenSessionId) {
          const match = this.allSessions.find(s => s._id === autoOpenSessionId);
          if (match && this.getSessionStatus(match) === 'upcoming') {
            setTimeout(() => this.openPopup(match), 400);
          }
        }

      } else {
        this.toast.showError('Failed to load live sessions');
        this.injectPageSchema();
      }
    },
    error: () => {
      this.toast.showError('Failed to load live sessions');
      this.injectPageSchema();
    }
  });
}

  setFilter(mode: FilterMode): void {
    this.activeFilter = mode;
    this.applyFilter();
  }

  private applyFilter(): void {
    if (this.activeFilter === 'all') {
      this.filteredSessions = [...this.allSessions];
      return;
    }
    this.filteredSessions = this.allSessions.filter((s) => this.getSessionStatus(s) === this.activeFilter);
  }

  getSessionStatus(event: LiveEvent): 'upcoming' | 'past' {
  const sessionMoment = this.getSessionStartDate(event);
  if (!sessionMoment) return 'past';

  const nowEST = moment.tz('America/New_York');

  return sessionMoment.isSameOrAfter(nowEST) ? 'upcoming' : 'past';
}

  isNextUp(event: LiveEvent): boolean {
    return !!this.nextSession && event._id === this.nextSession._id && this.getSessionStatus(event) === 'upcoming';
  }

  private pickNextSession(): void {
  const upcoming = this.allSessions
    .filter(s => this.getSessionStatus(s) === 'upcoming')
    .sort((a, b) => (this.getSessionStartDate(a)?.valueOf() ?? 0) - (this.getSessionStartDate(b)?.valueOf() ?? 0));

  this.nextSession = upcoming.length ? upcoming[0] : null;

  if (this.nextSession && this.getSessionStartDate(this.nextSession)) {
    this.startCountdown(this.nextSession);
    this.revealNext = true;
  } else {
    this.nextSession = null;
    this.stopCountdown();
    this.countdown = { days: '--', hrs: '--', mins: '--', secs: '--' };
  }
}

  private getSessionStartDate(event: LiveEvent): moment.Moment | null {
  const session = event?.sessions?.[0];
  if (!session?.date || !session?.startTime) return null;

  return moment.tz(
    `${session.date} ${session.startTime}`,
    'YYYY-MM-DD HH:mm',
    'America/New_York'
  );
}

  private startCountdown(event: LiveEvent): void {
  this.stopCountdown();

  const session = event.sessions?.[0];
  if (!session) return;

  const tick = () => {
    // ✅ Current time in EST (not user timezone)
    const nowEST = moment.tz('America/New_York');

    // ✅ Event time in EST (from API)
    const targetEST = moment.tz(
      `${session.date} ${session.startTime}`,
      'YYYY-MM-DD HH:mm',
      'America/New_York'
    );

    const diff = targetEST.diff(nowEST);

    if (diff <= 0) {
      this.countdown = { days: '00', hrs: '00', mins: '00', secs: '00' };
      return;
    }

    const duration = moment.duration(diff);

    const days  = Math.floor(duration.asDays());
const hrs   = duration.hours();
const mins  = duration.minutes();
const secs  = duration.seconds();
 
this.countdown = {
  days: String(days).padStart(2, '0'),
  hrs:  String(hrs).padStart(2, '0'),
  mins: String(mins).padStart(2, '0'),
  secs: String(secs).padStart(2, '0')
};

const newSummary = `${days} days, ${hrs} hours, ${mins} minutes remaining`;
if (newSummary !== this.countdownSummary) {
  this.countdownSummary = newSummary;
}
  };

  tick();
  this.countdownTimer = setInterval(tick, 1000);
}

  private stopCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  // ===== MODAL =====
  openPopup(session: LiveEvent): void {
  this.selectedSession = session;
  this.showPopup = true;
  this.modalStep = 'form';
  this.isSubmitting = false;

  this.firstName = '';
  this.lastName = '';
  this.email = '';

  this.emailError = false;
  this.emailFollowUp = false;
  this.generateHumanCheck();
}

  closePopup(): void {
  this.showPopup = false;
  this.selectedSession = null;
  this.firstName = '';
  this.lastName = '';
  this.email = '';
  this.modalStep = 'form';
  this.isSubmitting = false;

  this.emailError = false;
  this.humanCheckAnswer = null;
  this.humanCheckError = false;
  this.humanCheckRequiredError = false;
  this.emailFollowUp = false;
}

  @HostListener('document:keydown', ['$event'])
  onKeyDown(ev: KeyboardEvent): void {
    if (ev.key === 'Escape' && this.showPopup) this.closePopup();
  }

  registerForSession(): void {
  if (!this.selectedSession) return;

  this.emailError = false;
  this.humanCheckError = false;
  this.humanCheckRequiredError = false;

  if (!this.firstName.trim() || !this.lastName.trim() || !this.email.trim()) {
    this.toast.showError('All fields are required');
    return;
  }

  if (!this.isValidEmail(this.email)) {
    this.emailError = true;
    this.toast.showError('Please enter a valid email address');
    return;
  }

  if (
    this.humanCheckAnswer === null ||
    this.humanCheckAnswer === undefined ||
    String(this.humanCheckAnswer).trim() === ''
  ) {
    this.humanCheckRequiredError = true;
    this.toast.showError('Human Check is required');
    return;
  }

  if (!this.isHumanCheckValid()) {
    this.humanCheckError = true;
    this.toast.showError('Please enter the correct Human Check sum');
    this.generateHumanCheck();
    return;
  }

  this.isSubmitting = true;

  const payload = {
    eventId: this.selectedSession._id,
    sessionId: this.selectedSession?.sessions?.[0]?.id,
    firstName: this.firstName.trim(),
    lastName: this.lastName.trim(),
    email: this.email.trim(),
    emailFollowUp: this.emailFollowUp
  };

  this.commonService.registerForFreeSession(payload).subscribe({
    next: (res: any) => {
      this.isSubmitting = false;

      if (res?.status === 1 || String(res?.message || '').toLowerCase().includes('success')) {
        this.toast.showSuccess('Registration successful!');

        const match = this.allSessions.find((x) => x._id === this.selectedSession!._id);
        if (match) match.alreadyRegistered = true;

        this.modalStep = 'success';
        const selected = this.selectedSession!;

        this.successTitle = selected.title;
        this.successDate = this.formatDateEST(selected.sessions?.[0]?.date);
        this.successTime =
  `${this.formatTimeToEST(selected.sessions?.[0]?.startTime, false)} - ` +
  `${this.formatTimeToEST(selected.sessions?.[0]?.endTime)}`;
        this.successEmail = this.email.trim();

        this.applyFilter();
      } else {
        this.toast.showError(res?.message || 'Registration failed');
        this.generateHumanCheck();
      }
    },
    error: (err: any) => {
      this.isSubmitting = false;
      const msg = err?.error?.message || '';

      if (err.status === 409 || String(msg).toLowerCase().includes('already registered')) {
        this.toast.showSuccess('You are already registered for this session.');
        const match = this.allSessions.find((x) => x._id === this.selectedSession!._id);
        if (match) match.alreadyRegistered = true;
        this.applyFilter();
        this.closePopup();
      } else {
        this.toast.showError(err?.error?.message || 'Registration failed. Please try again.');
        this.generateHumanCheck();
      }
    }
  });
}


  formatTimeToEST(time?: string | null, showTZ: boolean = true): string {
  if (!time) return '';
  const parts = String(time).split(':');
  if (parts.length < 2) return String(time);
  const h = Number(parts[0]);
  const minutes = parts[1].padStart(2, '0');
  if (Number.isNaN(h)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${displayHour}:${minutes}${period}${showTZ ? ' EST' : ''}`;
}

  // ===== STYLE MAPPERS =====
  ngTextStyle(style?: any): { [k: string]: any } {
    if (!style) return {};
    const out: any = {};
    if (style.fontFamily) out['font-family'] = style.fontFamily;
    if (style.fontSize != null) out['font-size.px'] = style.fontSize;
    if (style.fontWeight != null) out['font-weight'] = style.fontWeight;
    if (style.lineHeight != null) out['line-height'] = style.lineHeight;
    if (style.letterSpacing != null) out['letter-spacing.px'] = style.letterSpacing;
    if (style.textTransform) out['text-transform'] = style.textTransform;
    if (style.textAlign) out['text-align'] = style.textAlign;
    if (style.color) out['color'] = style.color;
    if (style.maxWidth != null) out['max-width.px'] = style.maxWidth;
    return out;
  }

  ngLayoutStyle(layout?: LayoutStyle): { [k: string]: any } {
    if (!layout) return {};
    const out: any = {};
    if (layout.paddingTop != null) out['padding-top.px'] = layout.paddingTop;
    if (layout.paddingBottom != null) out['padding-bottom.px'] = layout.paddingBottom;
    if (layout.marginTop != null) out['margin-top.px'] = layout.marginTop;
    if (layout.marginBottom != null) out['margin-bottom.px'] = layout.marginBottom;
    if (layout.contentMaxWidth != null) out['max-width.px'] = layout.contentMaxWidth;
    if (layout.textAlign) out['text-align'] = layout.textAlign;

    const x = layout.offsetX ?? 0;
    const y = layout.offsetY ?? 0;
    if ((layout.offsetX != null && layout.offsetX !== 0) || (layout.offsetY != null && layout.offsetY !== 0)) {
      out['transform'] = `translate(${x}px, ${y}px)`;
    }
    return out;
  }

  // ✅ safe newline -> <br/>
  nl2br(text?: string | null): string {
    const t = String(text ?? '');
    const escaped = t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return escaped.replace(/\n/g, '<br/>');
  }

  // ===== Host preview (removes hardcoded Lyssa/Jake on cards) =====
  getHostInitialsPreview(): string[] {
    const hs = this.pageContent?.hostsSection?.hosts || [];
    return hs.slice(0, 2).map((h) => String(h.initials || '').trim()).filter(Boolean);
  }

  getHostNamesPreview(): string {
    const hs = this.pageContent?.hostsSection?.hosts || [];
    const names = hs.slice(0, 2).map((h) => String(h.name || '').trim()).filter(Boolean);
    return names.length ? names.join(' & ') : '';
  }

  private parseHeadlineLines(text: string): Array<{ before: string; em: string; after: string }> {
  const raw = String(text || '');
  const lines = raw.split('\n');

  return lines.map((line) => {
    const l = String(line || '');

    // Find first <em>...</em> in the line (admin controls exactly what is emphasized)
    const m = l.match(/<em>(.*?)<\/em>/i);

    if (!m) {
      return { before: l.trim(), em: '', after: '' };
    }

    const emText = (m[1] || '').trim();
    const before = l.slice(0, m.index ?? 0).trim();
    const after = l.slice((m.index ?? 0) + m[0].length).trim();

    return { before, em: emText, after };
  });
}


ngBoxStyle(style?: any): { [k: string]: any } {
  if (!style) return {};
  const out: any = {};

  if (style.backgroundColor) out['background-color'] = style.backgroundColor;
  if (style.color) out['color'] = style.color;

  if (style.borderColor) out['border-color'] = style.borderColor;
  if (style.borderWidth != null) out['border-width.px'] = style.borderWidth;
  if (style.borderStyle) out['border-style'] = style.borderStyle;
  if (style.borderRadius != null) out['border-radius.px'] = style.borderRadius;

  if (style.boxShadow) out['box-shadow'] = style.boxShadow;

  if (style.paddingTop != null) out['padding-top.px'] = style.paddingTop;
  if (style.paddingRight != null) out['padding-right.px'] = style.paddingRight;
  if (style.paddingBottom != null) out['padding-bottom.px'] = style.paddingBottom;
  if (style.paddingLeft != null) out['padding-left.px'] = style.paddingLeft;

  if (style.marginTop != null) out['margin-top.px'] = style.marginTop;
  if (style.marginBottom != null) out['margin-bottom.px'] = style.marginBottom;

  if (style.minHeight != null) out['min-height.px'] = style.minHeight;
  if (style.maxWidth != null) out['max-width.px'] = style.maxWidth;

  if (style.textAlign) out['text-align'] = style.textAlign;

  return out;
}

ngButtonStyle(style?: any): { [k: string]: any } {
  if (!style) return {};
  const out: any = {};

  if (style.backgroundColor) out['background-color'] = style.backgroundColor;
  if (style.textColor) out['color'] = style.textColor;
  if (style.borderColor) out['border-color'] = style.borderColor;
  if (style.borderWidth != null) out['border-width.px'] = style.borderWidth;
  if (style.borderRadius != null) out['border-radius.px'] = style.borderRadius;

  if (style.paddingTop != null) out['padding-top.px'] = style.paddingTop;
  if (style.paddingRight != null) out['padding-right.px'] = style.paddingRight;
  if (style.paddingBottom != null) out['padding-bottom.px'] = style.paddingBottom;
  if (style.paddingLeft != null) out['padding-left.px'] = style.paddingLeft;

  if (style.fontFamily) out['font-family'] = style.fontFamily;
  if (style.fontSize != null) out['font-size.px'] = style.fontSize;
  if (style.fontWeight != null) out['font-weight'] = style.fontWeight;
  if (style.letterSpacing != null) out['letter-spacing.px'] = style.letterSpacing;
  if (style.textTransform) out['text-transform'] = style.textTransform;

  if (style.boxShadow) out['box-shadow'] = style.boxShadow;

  return out;
}

getSessionCardStyle(event: LiveEvent): any {
  return this.isNextUp(event)
    ? this.ngBoxStyle(this.pageContent?.sessionsSection?.featuredCardStyle)
    : this.ngBoxStyle(this.pageContent?.sessionsSection?.cardStyle);
}

getSessionBadgeStyle(event: LiveEvent): any {
  const base = this.ngTextStyle(this.pageContent?.sessionsSection?.badgeStyle);

  if (this.isNextUp(event)) {
    return {
      ...base,
      ...this.ngBoxStyle(this.pageContent?.sessionsSection?.badgeNextStyle)
    };
  }

  if (this.getSessionStatus(event) === 'upcoming') {
    return {
      ...base,
      ...this.ngBoxStyle(this.pageContent?.sessionsSection?.badgeUpcomingStyle)
    };
  }

  return {
    ...base,
    ...this.ngBoxStyle(this.pageContent?.sessionsSection?.badgePastStyle)
  };
}

mergeStyles(...styles: Array<{ [key: string]: any } | null | undefined>): { [key: string]: any } {
  return Object.assign({}, ...styles.filter(Boolean));
}



// get upcomingSessionSlots(): (LiveEvent | null)[] {
//   const upcoming = this.filteredSessions.filter(s => this.getSessionStatus(s) === 'upcoming');
//   return [upcoming[0] ?? null, upcoming[1] ?? null];
// }

get upcomingSessionSlots(): (LiveEvent | null)[] {
  const upcoming = this.filteredSessions.filter(s => this.getSessionStatus(s) === 'upcoming');
  // Always append one "Coming Soon" placeholder at the end
  return [...upcoming, null];
}

private generateHumanCheck(): void {
  this.humanCheckNum1 = Math.floor(Math.random() * 9) + 1;
  this.humanCheckNum2 = Math.floor(Math.random() * 9) + 1;
  this.humanCheckAnswer = null;
  this.humanCheckError = false;
  this.humanCheckRequiredError = false;
}

private isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  return emailRegex.test((email || '').trim());
}

private isHumanCheckValid(): boolean {
  return Number(this.humanCheckAnswer) === (this.humanCheckNum1 + this.humanCheckNum2);
}

formatDateEST(date?: string | Date | null, fmt: string = 'dddd, MMMM D, YYYY'): string {
  if (!date) return '';
  const dateStr = date instanceof Date
    ? moment(date).format('YYYY-MM-DD')
    : String(date);
  return moment.tz(dateStr, 'YYYY-MM-DD', 'America/New_York').format(fmt);
}

private getSessionIdFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('session');
}

copySessionLink(session: LiveEvent): void {
  const url = `${window.location.origin}${window.location.pathname}?session=${session._id}`;
  navigator.clipboard.writeText(url).then(() => {
    this.toast.showSuccess('Session link copied to clipboard!');
  }).catch(() => {
    const textarea = document.createElement('textarea');
    textarea.value = url;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    this.toast.showSuccess('Session link copied to clipboard!');
  });
}



private injectPageSchema(): void {
  const upcomingEvents = this.allSessions.filter(
    s => this.getSessionStatus(s) === 'upcoming'
  );
 
  const graph = LiveSessionSchemaBuilder.buildGraph(upcomingEvents);
 
  this.seoService.injectSchema(graph);
}
}